// Gamification service — XP, coins, streaks, levels, daily goals.
// State lives in localStorage for instant UI and is synced to the server
// (Firestore) whenever the user is logged in, powering the global leaderboard.
import { reactive } from 'vue';
import { GAME_API, authHeaders } from './api';
import { hydrateGuest, queueGuestSync, claimGuestDevice } from './guestSync';

const STORAGE_KEY = 'sv_game_state';

// LOCAL date strings — toISOString() is UTC, which would make the "day"
// roll over at 5:30 AM for students in India instead of midnight.
function dateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayStr() {
  return dateStr(new Date());
}

function yesterdayStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return dateStr(d);
}

function load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

export const gameState = reactive({
  xp: 0,
  coins: 0,
  streak: 0,
  lastActiveDay: null,
  todayXP: 0,
  todayDate: todayStr(),
  dailyGoal: 50,
  earnedBadges: [],
  shields: 0,
  avatar: '🦚',
  activeDays: [],   // recent LOCAL date strings, powers the streak calendar
  weekXp: 0,        // XP earned this ISO week — drives the league division
  weekId: null,
  ...load()
});

// ── Weekly bucket (mirrors the server's ISO-week rollover) ──
function isoWeekId(d = new Date()) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayNum = date.getDay() || 7;
  date.setDate(date.getDate() + 4 - dayNum);
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// Was the student active on this local date? (used by the streak calendar)
export function wasActiveOn(dateStr) {
  return Array.isArray(gameState.activeDays) && gameState.activeDays.includes(dateStr);
}

// The last `n` days, most recent last: [{ date, active, isToday }]
export function recentDays(n = 7) {
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const ds = dateStr(d);
    out.push({ date: ds, active: wasActiveOn(ds), isToday: i === 0, dow: d.getDay() });
  }
  return out;
}

// ── Avatars ──
// Small curated set; the id synced to the server is the emoji itself,
// validated against this same list server-side.
export const AVATARS = ['🦚', '🐯', '🐘', '🦜', '🐒', '🦁', '🐼', '🦋', '🐢', '🦉', '🐬', '🦄', '🚀', '⚽', '🎸', '🌟'];

export function setAvatar(a) {
  if (!AVATARS.includes(a)) return;
  gameState.avatar = a;
  persist();
  syncToServer();
}

// ── Streak shields ──
// Bought with coins; if the player misses a day, one shield is spent
// automatically and the streak survives.
export const SHIELD_COST = 50;
export const SHIELD_MAX = 2;

export function buyShield() {
  if (gameState.coins < SHIELD_COST || gameState.shields >= SHIELD_MAX) return false;
  gameState.coins -= SHIELD_COST;
  gameState.shields += 1;
  persist();
  syncToServer();
  return true;
}

// Badge popups waiting to be shown — App.vue watches this queue
export const badgeQueue = reactive([]);

// Full-screen celebrations waiting to be shown (currently level-ups).
// Separate from badgeQueue so a small toast and a big modal never fight
// over the same slot. Entries: { type:'level', level, coins }
export const celebrationQueue = reactive([]);

// Coin bonus paid out the moment a student crosses into a new level.
export function levelUpBonus(level) {
  return 5 + level * 2;
}

// ── Achievements ──
export const BADGES = [
  { id: 'first-steps', icon: '🐣', name: { en: 'First Steps', hi: 'पहला कदम' }, desc: { en: 'Earn your first XP', hi: 'अपना पहला XP कमाएँ' }, check: s => s.xp >= 1 },
  { id: 'goal-getter', icon: '🎯', name: { en: 'Goal Getter', hi: 'लक्ष्य वीर' }, desc: { en: 'Reach your daily goal', hi: 'आज का लक्ष्य पूरा करें' }, check: s => s.todayXP >= s.dailyGoal },
  { id: 'streak-3', icon: '🔥', name: { en: 'On Fire', hi: 'आग लगा दी' }, desc: { en: '3-day streak', hi: '3 दिन की लगन' }, check: s => s.streak >= 3 },
  { id: 'streak-7', icon: '🚀', name: { en: 'Unstoppable', hi: 'अजेय' }, desc: { en: '7-day streak', hi: '7 दिन की लगन' }, check: s => s.streak >= 7 },
  { id: 'streak-30', icon: '👑', name: { en: 'Legend', hi: 'महारथी' }, desc: { en: '30-day streak', hi: '30 दिन की लगन' }, check: s => s.streak >= 30 },
  { id: 'xp-250', icon: '⭐', name: { en: 'Rising Star', hi: 'उभरता सितारा' }, desc: { en: 'Earn 250 XP', hi: '250 XP कमाएँ' }, check: s => s.xp >= 250 },
  { id: 'xp-1000', icon: '🌟', name: { en: 'Superstar', hi: 'सुपरस्टार' }, desc: { en: 'Earn 1000 XP', hi: '1000 XP कमाएँ' }, check: s => s.xp >= 1000 },
  { id: 'xp-5000', icon: '💫', name: { en: 'Galaxy Brain', hi: 'महाज्ञानी' }, desc: { en: 'Earn 5000 XP', hi: '5000 XP कमाएँ' }, check: s => s.xp >= 5000 },
  { id: 'level-5', icon: '🏅', name: { en: 'High Five', hi: 'हाई फाइव' }, desc: { en: 'Reach level 5', hi: 'स्तर 5 तक पहुँचें' }, check: s => levelForXP(s.xp) >= 5 },
  { id: 'level-10', icon: '🏆', name: { en: 'Perfect Ten', hi: 'दस का दम' }, desc: { en: 'Reach level 10', hi: 'स्तर 10 तक पहुँचें' }, check: s => levelForXP(s.xp) >= 10 },
  { id: 'coins-100', icon: '🪙', name: { en: 'Coin Collector', hi: 'सिक्का संग्रहकर्ता' }, desc: { en: 'Collect 100 coins', hi: '100 सिक्के जमा करें' }, check: s => s.coins >= 100 },
  { id: 'arena-500', icon: '⚡', name: { en: 'Speed Demon', hi: 'बिजली की रफ़्तार' }, desc: { en: 'Score 500+ in the Arena', hi: 'अखाड़े में 500+ स्कोर करें' }, check: () => Number(localStorage.getItem('arenaBest') || 0) >= 500 },
  { id: 'arena-1500', icon: '🥇', name: { en: 'Arena Champion', hi: 'अखाड़ा चैंपियन' }, desc: { en: 'Score 1500+ in the Arena', hi: 'अखाड़े में 1500+ स्कोर करें' }, check: () => Number(localStorage.getItem('arenaBest') || 0) >= 1500 }
];

function checkBadges() {
  if (!Array.isArray(gameState.earnedBadges)) gameState.earnedBadges = [];
  for (const b of BADGES) {
    if (!gameState.earnedBadges.includes(b.id) && b.check(gameState)) {
      gameState.earnedBadges.push(b.id);
      badgeQueue.push({ icon: b.icon, name: b.name, desc: b.desc });
    }
  }
}

// XP thresholds grow gently: level n needs n*100 XP total-ish curve
export function levelForXP(xp) {
  return Math.floor(Math.sqrt(xp / 60)) + 1;
}

export function levelProgress(xp) {
  const lvl = levelForXP(xp);
  const start = (lvl - 1) * (lvl - 1) * 60;
  const end = lvl * lvl * 60;
  return Math.min(100, Math.round(((xp - start) / (end - start)) * 100));
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    xp: gameState.xp,
    coins: gameState.coins,
    streak: gameState.streak,
    lastActiveDay: gameState.lastActiveDay,
    todayXP: gameState.todayXP,
    todayDate: gameState.todayDate,
    dailyGoal: gameState.dailyGoal,
    earnedBadges: gameState.earnedBadges,
    shields: gameState.shields,
    avatar: gameState.avatar,
    activeDays: gameState.activeDays,
    weekXp: gameState.weekXp,
    weekId: gameState.weekId
  }));
}

function rollDay() {
  const today = todayStr();
  if (gameState.todayDate !== today) {
    gameState.todayDate = today;
    gameState.todayXP = 0;
  }
}

let syncTimer = null;

// Debounced: rapid awards (e.g. reading several Wonder facts quickly) send
// ONE sync with the final totals instead of racing overlapping POSTs.
function syncToServer() {
  // No account: back the progress up against this browser's device id instead,
  // so a guest who returns tomorrow (or loses localStorage but keeps cookies)
  // still finds their XP where they left it.
  if (!localStorage.getItem('authToken')) {
    persist();
    queueGuestSync();
    return;
  }
  clearTimeout(syncTimer);
  syncTimer = setTimeout(syncNow, 1200);
}

async function syncNow() {
  const token = localStorage.getItem('authToken');
  if (!token) return;
  try {
    await fetch(`${GAME_API}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        xp: gameState.xp,
        streak: gameState.streak,
        coins: gameState.coins,
        avatar: gameState.avatar,
        // These belong to the account, not the browser — without them, signing
        // in on a phone after setting up on a laptop re-ran onboarding and
        // reset the student's class back to the default.
        grade: Number(localStorage.getItem('myGrade')) || null,
        interests: getInterests(),
        onboarded: localStorage.getItem('onboarded') === '1',
        lang: localStorage.getItem('siteLanguage') || null
      })
    });
  } catch (e) {
    // offline is fine — will sync on next award
  }
}

// Rebuild the reactive stores from localStorage.
//
// gameState and questState are built once at import time. If something rewrites
// localStorage afterwards — a guest restore pulling this device's backup down
// from the server — the in-memory copies are stale, and the next persist()
// would write the stale values straight back over what was just restored.
function reloadLocalState() {
  Object.assign(gameState, {
    xp: 0, coins: 0, streak: 0, lastActiveDay: null,
    todayXP: 0, todayDate: todayStr(), dailyGoal: 50,
    earnedBadges: [], shields: 0, avatar: '🦚',
    activeDays: [], weekXp: 0, weekId: null,
    ...load()
  });
  Object.assign(questState, loadQuestState());
  refreshInterests();
}

// Pull server copy (e.g. after login on a new device) and keep the higher XP.
export async function pullFromServer() {
  const token = localStorage.getItem('authToken');
  if (!token) {
    // Guests get the same treatment against their device backup.
    if (await hydrateGuest()) reloadLocalState();
    return;
  }
  // Push local progress first (server keeps max), so an offline session's
  // XP isn't discarded when the server copy happens to be higher.
  await syncNow();
  try {
    const res = await fetch(`${GAME_API}/profile`, { headers: authHeaders() });
    if (!res.ok) return;
    const data = await res.json();
    if (data && typeof data.xp === 'number' && data.xp > gameState.xp) {
      gameState.xp = data.xp;
      gameState.coins = Math.max(gameState.coins, data.coins || 0);
      gameState.streak = Math.max(gameState.streak, data.streak || 0);
      persist();
    }
    // restore avatar on a fresh device
    if (data && data.avatar && AVATARS.includes(data.avatar) && gameState.avatar === '🦚') {
      gameState.avatar = data.avatar;
      persist();
    }
    // ...and the account's own settings, so a second device picks up where the
    // first left off instead of asking for the class all over again. Local
    // values win — this only fills in what this browser does not know yet.
    if (data) {
      if (data.grade && !localStorage.getItem('myGrade')) {
        localStorage.setItem('myGrade', String(data.grade));
      }
      if (data.lang && !localStorage.getItem('siteLanguage')) {
        localStorage.setItem('siteLanguage', data.lang);
      }
      if (Array.isArray(data.interests) && data.interests.length && !localStorage.getItem(INTERESTS_KEY)) {
        localStorage.setItem(INTERESTS_KEY, JSON.stringify(data.interests));
        refreshInterests();
      }
      if (data.onboarded && localStorage.getItem('onboarded') !== '1') {
        localStorage.setItem('onboarded', '1');
      }
    }
  } catch (e) {
    // ignore network errors
  }
}

/**
 * Award XP for completing an activity.
 * Updates streak: first activity of the day extends (or resets) it.
 * On a level-up, pays a coin bonus and queues the celebration modal
 * (see celebrationQueue), so every activity gets the feedback for free.
 * Returns { xp, leveledUp, newLevel, levelBonus, streakExtended }
 */
export function awardXP(amount, coins = 0) {
  rollDay();
  const before = levelForXP(gameState.xp);
  const today = todayStr();

  let streakExtended = false;
  if (gameState.lastActiveDay !== today) {
    gameState.streak = gameState.lastActiveDay === yesterdayStr()
      ? gameState.streak + 1
      : 1;
    gameState.lastActiveDay = today;
    streakExtended = true;
  }

  // remember the day for the streak calendar (keep ~5 weeks)
  if (!Array.isArray(gameState.activeDays)) gameState.activeDays = [];
  if (!gameState.activeDays.includes(today)) {
    gameState.activeDays.push(today);
    if (gameState.activeDays.length > 35) gameState.activeDays.splice(0, gameState.activeDays.length - 35);
  }

  // weekly bucket for the league division — resets with the ISO week
  const wk = isoWeekId();
  if (gameState.weekId !== wk) { gameState.weekId = wk; gameState.weekXp = 0; }
  gameState.weekXp += amount;

  gameState.xp += amount;
  gameState.todayXP += amount;
  gameState.coins += coins;

  const after = levelForXP(gameState.xp);

  // Level-up! Pay the coin bonus and queue the celebration modal. Done
  // before checkBadges() so the coin badges can see the bonus, and before
  // persist() so the extra coins are saved with the rest of the award.
  let levelBonus = 0;
  if (after > before) {
    levelBonus = levelUpBonus(after);
    gameState.coins += levelBonus;
    celebrationQueue.push({ type: 'level', level: after, coins: levelBonus });
  }

  checkBadges();
  questTick('xp', amount); // feeds the "earn N XP today" quest
  persist();
  syncToServer();

  return {
    xp: gameState.xp,
    leveledUp: after > before,
    newLevel: after,
    levelBonus,
    streakExtended
  };
}

// ── Daily Quests ────────────────────────────────────────────────
// Three missions per day, picked deterministically from the pool by
// date, so every player sees the same quests. Completing all three
// pays a bonus chest. Progress lives in localStorage.
const QUEST_POOL = [
  { id: 'math10', type: 'math-correct', target: 10, icon: '🔢', name: { en: 'Answer 10 maths questions correctly', hi: '10 गणित के सवाल सही हल करो' } },
  { id: 'arena1', type: 'arena-round', target: 1, icon: '⚡', name: { en: 'Finish an Arena round', hi: 'अखाड़े का एक राउंड पूरा करो' } },
  { id: 'wonder3', type: 'fact', target: 3, icon: '🤯', name: { en: 'Discover 3 new wonders', hi: '3 नए अजूबे खोजो' } },
  { id: 'quiz5', type: 'quiz-correct', target: 5, icon: '📚', name: { en: 'Get 5 lesson quiz answers right', hi: 'पाठ क्विज़ में 5 सही जवाब दो' } },
  { id: 'tables15', type: 'tables-correct', target: 15, icon: '✖️', name: { en: 'Solve 15 table questions', hi: 'पहाड़े के 15 सवाल हल करो' } },
  { id: 'song1', type: 'song', target: 1, icon: '🎤', name: { en: 'Sing one song', hi: 'एक गाना गाओ' } },
  { id: 'exp1', type: 'experiment', target: 1, icon: '🧪', name: { en: 'Complete a Wonder Lab experiment', hi: 'जिज्ञासा लैब का एक प्रयोग पूरा करो' } },
  { id: 'puzzle2', type: 'puzzle', target: 2, icon: '♟️', name: { en: 'Solve 2 chess puzzles', hi: 'शतरंज की 2 पहेलियाँ हल करो' } },
  // Yoga and the chess coach paid nothing and had no quest, so a student
  // whose picked interests were those never moved the daily goal at all.
  { id: 'pose3', type: 'pose', target: 3, icon: '🧘', name: { en: 'Hold 3 yoga poses', hi: 'तीन योग आसन करो' } },
  { id: 'coach1', type: 'coach-game', target: 1, icon: '🎓', name: { en: 'Finish a game vs the coach', hi: 'कोच के साथ एक खेल पूरा करो' } },
  { id: 'sudoku1', type: 'sudoku', target: 1, icon: '🔢', name: { en: 'Finish a Sudoku', hi: 'एक सुडोकू पूरा करो' } },
  { id: 'streak1', type: 'any-xp', target: 30, icon: '💎', name: { en: 'Earn 30 XP today', hi: 'आज 30 XP कमाओ' } }
];

const QUEST_KEY = 'sv_quests';

function questSeededIndexes(dayNumber) {
  // simple deterministic pick of 3 distinct pool indexes for the day
  const picks = [];
  let x = dayNumber;
  while (picks.length < 3) {
    x = (x * 9301 + 49297) % 233280;
    const idx = x % QUEST_POOL.length;
    if (!picks.includes(idx)) picks.push(idx);
  }
  return picks;
}

function loadQuestState() {
  const today = todayStr();
  let st;
  try { st = JSON.parse(localStorage.getItem(QUEST_KEY)); } catch { st = null; }
  if (!st || st.date !== today) {
    const day = Math.floor(Date.now() / 86400000);
    st = { date: today, ids: questSeededIndexes(day).map(i => QUEST_POOL[i].id), progress: {}, claimed: false };
    localStorage.setItem(QUEST_KEY, JSON.stringify(st));
  }
  return st;
}

export const questState = reactive(loadQuestState());

function persistQuests() {
  localStorage.setItem(QUEST_KEY, JSON.stringify({
    date: questState.date, ids: questState.ids,
    progress: questState.progress, claimed: questState.claimed
  }));
}

export function todaysQuests() {
  // roll to a new day if needed
  const fresh = loadQuestState();
  if (fresh.date !== questState.date) {
    Object.assign(questState, fresh);
  }
  return questState.ids
    .map(id => QUEST_POOL.find(q => q.id === id))
    .filter(Boolean)
    .map(q => ({ ...q, done: Math.min(questState.progress[q.id] || 0, q.target) }));
}

export function questsAllDone() {
  return todaysQuests().every(q => q.done >= q.target);
}

// Called by activities: questTick('math-correct'), questTick('fact')…
export function questTick(type, n = 1) {
  const quests = todaysQuests();
  let changed = false;
  for (const q of quests) {
    const matches = q.type === type || (q.type === 'any-xp' && type === 'xp');
    if (matches && (questState.progress[q.id] || 0) < q.target) {
      questState.progress[q.id] = (questState.progress[q.id] || 0) + n;
      changed = true;
    }
  }
  if (changed) persistQuests();
}

// Claim the chest once all three are complete. Returns reward or null.
export function claimQuestChest() {
  if (questState.claimed || !questsAllDone()) return null;
  questState.claimed = true;
  persistQuests();
  awardXP(40, 10);
  return { xp: 40, coins: 10 };
}

// Check streak on app load — if user skipped a day, a shield saves it,
// otherwise the streak dies. One shield covers the whole gap.
// Returns 'saved' when a shield was consumed so the UI can celebrate.
export function refreshStreak() {
  rollDay();
  if (gameState.lastActiveDay &&
      gameState.lastActiveDay !== todayStr() &&
      gameState.lastActiveDay !== yesterdayStr()) {
    if (gameState.shields > 0 && gameState.streak > 0) {
      gameState.shields -= 1;
      // pretend yesterday was active so today's first XP extends the streak
      gameState.lastActiveDay = yesterdayStr();
      persist();
      return 'saved';
    }
    gameState.streak = 0;
    persist();
  }
  return null;
}

// ── Mistake deck ────────────────────────────────────────────────
// Wrong answers from solo practice are stored so students can replay
// exactly the questions they got wrong. Deduped by question text.
// ── What the student actually wants to learn ────────────────
// The site has a lot of surfaces. Rather than show every one to every child,
// they pick their interests during onboarding and Home leads with those; the
// rest stay one tap away behind "Show everything". An empty list means "all",
// so nobody who skips the step loses access to anything.
// ── Guests ───────────────────────────────────────────
// Everything the gamification loop needs already lives in localStorage, so a
// visitor can play, earn XP, build a streak and finish quests with no account
// at all. An account only buys them durability: progress that survives a
// cleared browser, follows them to their phone, and appears on the leaderboard.
export function isGuest() {
  return !localStorage.getItem('authToken');
}

/**
 * Hand a guest's local progress to the account they just made.
 *
 * Called right after a successful register/login. Without this, a visitor who
 * played for twenty minutes and then signed up would watch their XP, streak and
 * quests reset to zero — the single worst moment to punish someone.
 *
 * On LOGIN we keep whichever is larger, because the account may already hold
 * more progress than this browser does.
 */
export function adoptGuestProgress() {
  try {
    rollDay();
    persist();          // make sure the in-memory state is on disk first
    syncNow();          // then push it up under the new token
    // Stop the guest writer and mark the device row claimed, so it is never
    // restored on top of a real account and can be pruned later.
    claimGuestDevice(localStorage.getItem('authToken'));
  } catch { /* never block sign-in on a sync problem */ }
}

const INTERESTS_KEY = 'sv_interests';

export const INTERESTS = [
  { id: 'maths',   icon: '➗',    routes: ['/math-path', '/academics/maths'],                 name: { en: 'Maths',           hi: 'गणित' } },
  { id: 'science', icon: '🔬', routes: ['/academics/science'],                            name: { en: 'Science',         hi: 'विज्ञान' } },
  { id: 'english', icon: '📖', routes: ['/academics/english'],                            name: { en: 'English',         hi: 'अंग्रेज़ी' } },
  { id: 'puzzles', icon: '🧩', routes: ['/sudoku'],                                       name: { en: 'Puzzles & logic', hi: 'पहेलियाँ' } },
  { id: 'chess',   icon: '♟️', routes: ['/puzzles', '/multiplayer'],                    name: { en: 'Chess',           hi: 'शतरंज' } },
  { id: 'music',   icon: '🎤', routes: ['/song-practice', '/instruments'],                name: { en: 'Music & singing', hi: 'संगीत' } },
  { id: 'yoga',    icon: '🧘', routes: ['/yoga'],                                         name: { en: 'Yoga & movement', hi: 'योग' } },
  { id: 'wonder',  icon: '🧪', routes: ['/wonder'],                                       name: { en: 'Experiments',     hi: 'प्रयोग' } }
];

// Routes that everyone sees no matter what they picked.
export const ALWAYS_SHOWN = ['/arena', '/leaderboard'];

function readInterests() {
  try {
    const v = JSON.parse(localStorage.getItem(INTERESTS_KEY));
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

// Reactive, NOT a bare localStorage read. A computed that reads localStorage
// directly never re-evaluates when the value changes, so Home kept showing the
// pre-onboarding list until a hard reload.
export const prefs = reactive({ interests: readInterests() });

export function getInterests() {
  return prefs.interests;
}

export function setInterests(ids) {
  const valid = INTERESTS.map(i => i.id);
  const clean = (Array.isArray(ids) ? ids : []).filter(x => valid.includes(x));
  localStorage.setItem(INTERESTS_KEY, JSON.stringify(clean));
  prefs.interests = clean;
  syncToServer();
  return clean;
}

// Called after a server pull writes the key behind our back.
export function refreshInterests() {
  prefs.interests = readInterests();
}

// Which routes this student has asked to see. No picks => everything.
export function interestedRoutes() {
  const picked = prefs.interests;
  if (!picked.length) return null;
  const routes = new Set(ALWAYS_SHOWN);
  INTERESTS.filter(i => picked.includes(i.id)).forEach(i => i.routes.forEach(r => routes.add(r)));
  return routes;
}

const MISTAKES_KEY = 'sv_mistakes';
const MISTAKES_MAX = 40;

export function getMistakes() {
  try { return JSON.parse(localStorage.getItem(MISTAKES_KEY)) || []; } catch { return []; }
}

// Stores the full question object (same shape the Arena renders), deduped
// by its English text, newest first.
export function addMistake(question, subject) {
  if (!question || !question.en) return;
  const list = getMistakes().filter(x => x.en !== question.en);
  list.unshift({ ...question, subject: subject || 'math' });
  localStorage.setItem(MISTAKES_KEY, JSON.stringify(list.slice(0, MISTAKES_MAX)));
}

export function removeMistake(enText) {
  const list = getMistakes().filter(x => x.en !== enText);
  localStorage.setItem(MISTAKES_KEY, JSON.stringify(list));
}
